import { useEffect, useState, useCallback } from "react";
import type { Tweaks } from "../types";

const STORAGE_KEY = "pde-writer-tweaks";

export const DEFAULT_TWEAKS: Tweaks = {
  density: "comfortable",
  mock: false, // D-33: first-load default uses real backend; user can still toggle in Tweaks panel
  showGrid: false,
  displayScale: 1,
};

function load(): Tweaks {
  if (typeof window === "undefined") return DEFAULT_TWEAKS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_TWEAKS;
    const parsed = JSON.parse(raw) as Partial<Tweaks>;
    return { ...DEFAULT_TWEAKS, ...parsed };
  } catch {
    return DEFAULT_TWEAKS;
  }
}

export function useTweaks(): [Tweaks, (next: Tweaks | ((prev: Tweaks) => Tweaks)) => void] {
  const [tweaks, setTweaksState] = useState<Tweaks>(() => load());

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(tweaks));
    } catch {
      /* quota or disabled storage — silent fallback */
    }
  }, [tweaks]);

  const setTweaks = useCallback(
    (next: Tweaks | ((prev: Tweaks) => Tweaks)) => {
      setTweaksState((prev) =>
        typeof next === "function" ? (next as (p: Tweaks) => Tweaks)(prev) : next,
      );
    },
    [],
  );

  return [tweaks, setTweaks];
}
